import React, { useEffect, useState } from 'react'
import { getPublicCoupons, getMyCouponUsage, applyCouponToCart } from '../../clients/coupon'
import { getCartId } from '../../clients/cartNew'
import { fetchProductByProviderIdAndSku } from '../../clients/product'
import { formatCurrency } from '../../utils/intl'

/**
 * Sección de cupones marketplace v2 dentro de la pantalla "Cupones".
 * Autocontenida: lista los cupones públicos vigentes (a qué proveedor/marca/SKU
 * aplican, con botón para aplicarlos al carro activo) y el historial de usos.
 * No toca la lógica legacy.
 */

const nivelLabel: { [k: string]: string } = { proveedor: 'Proveedor', marca: 'Marca', sku: 'SKU' }

const descuentoText = (c: any) =>
  c?.tipo_descuento === 'porcentaje'
    ? `${c.valor_descuento}%${c.descuento_max_clp ? ` (tope ${formatCurrency(c.descuento_max_clp)})` : ''}`
    : formatCurrency(c?.valor_descuento || 0)

// Texto legible del ámbito: a qué aplica el cupón. Usa los nombres que enriquece
// el BFF (proveedor_nombre/marca_nombre/sku_productos); si faltan, cae al genérico.
const ambitoText = (c: any): string => {
  const prov = c?.proveedor_nombre
  if (c?.nivel === 'marca') {
    const marca = c?.marca_nombre || 'marca específica'
    return `Marca: ${marca}${prov ? ` · ${prov}` : ''}`
  }
  if (c?.nivel === 'sku') {
    // Preferimos mostrar el NOMBRE del producto (con su SKU de referencia);
    // si el BFF no lo pudo resolver, caemos al código SKU y luego al conteo.
    let detalle: string
    if (Array.isArray(c?.sku_productos) && c.sku_productos.length) {
      // Solo el nombre del producto (sin el código SKU).
      detalle = c.sku_productos
        .map((p: any) => p?.nombre || p?.sku)
        .filter(Boolean)
        .join(', ')
    } else if (Array.isArray(c?.sku_codigos) && c.sku_codigos.length) {
      detalle = c.sku_codigos.join(', ')
    } else if (Array.isArray(c?.skus) && c.skus.length) {
      detalle = c.skus.join(', ')
    } else {
      detalle = `${(c?.skus || []).length} producto(s)`
    }
    return `Productos: ${detalle}${prov ? ` · ${prov}` : ''}`
  }
  return `Proveedor: ${prov || nivelLabel[c?.nivel] || '—'}`
}

// En qué comunas está disponible el cupón. Vacío = todas. Usa los nombres que
// enriquece el BFF (comunas_nombres); si faltan, muestra el conteo.
const comunasText = (c: any): string => {
  const nombres = Array.isArray(c?.comunas_nombres) ? c.comunas_nombres : []
  if (Array.isArray(c?.comunas) && c.comunas.length) {
    return nombres.length
      ? `Válido en: ${nombres.join(', ')}`
      : `Válido en ${c.comunas.length} comuna(s)`
  }
  return 'Válido en todas las comunas'
}

type Feedback = { ok: boolean; text: string }

const CouponV2Section: React.FC<{ history?: any }> = ({ history }) => {
  const [publicos, setPublicos] = useState<any[]>([])
  const [usos, setUsos] = useState<any[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [applying, setApplying] = useState<string>('')
  const [feedback, setFeedback] = useState<{ [codigo: string]: Feedback | undefined }>({})

  useEffect(() => {
    let mounted = true
    const load = async () => {
      try {
        const [pub, mis] = await Promise.all([
          getPublicCoupons().catch(() => null),
          getMyCouponUsage().catch(() => null),
        ])
        if (!mounted) return
        setPublicos(pub?.docs || (Array.isArray(pub) ? pub : []))
        setUsos(mis?.docs || (Array.isArray(mis) ? mis : []))
      } finally {
        if (mounted) setLoading(false)
      }
    }
    load()
    return () => {
      mounted = false
    }
  }, [])

  const aplicar = async (c: any) => {
    setApplying(c.codigo)
    setFeedback((prev) => ({ ...prev, [c.codigo]: undefined }))
    try {
      // getCartId() devuelve { data: { _id, ... } } (ver uso en Card.tsx/CartNew.tsx).
      const res: any = await getCartId()
      const cartId = res?.data?._id || res?.data?.id || res?._id || res?.id || (typeof res === 'string' ? res : null)
      if (!cartId) {
        setFeedback((prev) => ({ ...prev, [c.codigo]: { ok: false, text: 'Agrega productos al carro primero' } }))
        return
      }
      const result: any = await applyCouponToCart(cartId, c.codigo)
      // El backend puede responder 2xx con { ok:false, motivo, mensaje } cuando el
      // cupón NO aplica (fuera de comuna, bajo mínimo, ya hay otro cupón, etc.).
      // No damos por aplicado sin verificar: mostramos el motivo real para no
      // confundir al cliente con un "✓" falso.
      if (result && (result.ok === false || result.motivo || result.aplica === false)) {
        setFeedback((prev) => ({
          ...prev,
          [c.codigo]: { ok: false, text: result.mensaje || result.motivo || 'No se pudo aplicar el cupón' },
        }))
        return
      }
      setFeedback((prev) => ({ ...prev, [c.codigo]: { ok: true, text: 'Cupón aplicado al carro ✓' } }))
    } catch (e: any) {
      const data = e?.response?.data
      const msg =
        (data && (data.mensaje || data.motivo)) ||
        (typeof data === 'string' && data) ||
        'No se pudo aplicar el cupón'
      setFeedback((prev) => ({ ...prev, [c.codigo]: { ok: false, text: msg } }))
    } finally {
      setApplying('')
    }
  }

  // Cuántas veces canjeó el usuario este cupón (para mostrar la cantidad).
  const usoCuponId = (u: any) => String(u?.cupon_id?._id || u?.cupon_id || '')
  const usosCanjeadosDe = (couponId: string) =>
    usos.filter((u: any) => u?.estado === 'canjeado' && usoCuponId(u) === String(couponId)).length
  const usosText = (c: any) => {
    const usados = usosCanjeadosDe(c._id)
    return c.usos_maximos_por_usuario == null
      ? `Usaste ${usados}`
      : `Usaste ${usados} de ${c.usos_maximos_por_usuario}`
  }

  // Navega al detalle del producto. ProductModal exige el producto completo en
  // location.state (si no, redirige a /home), así que lo traemos por sku+proveedor.
  const goToProduct = async (proveedorId: string, sku: string, productId?: string) => {
    if (!proveedorId || !sku || !history) return
    try {
      const product = await fetchProductByProviderIdAndSku(proveedorId, sku)
      if (!product) return
      const originLink = window.location.pathname.split('/')
      history.push(`/vendor/${proveedorId}/product/${productId || (product as any)._id}`, {
        product,
        from: 'fromDeeplink',
        originPath: originLink[1],
        originLink,
      })
    } catch (e) {
      /* si falla la carga, no navega */
    }
  }

  // Ámbito del cupón. Para nivel SKU, los productos son enlaces navegables (solo
  // el nombre). Para proveedor/marca, texto plano.
  const renderAmbito = (c: any) => {
    if (c?.nivel === 'sku' && Array.isArray(c?.sku_productos) && c.sku_productos.length) {
      const prov = c?.proveedor_nombre
      return (
        <span>
          Productos:{' '}
          {c.sku_productos.map((p: any, i: number) => (
            <React.Fragment key={p.sku || i}>
              {i > 0 ? ', ' : ''}
              {p.sku ? (
                <span
                  onClick={() => goToProduct(c.proveedor_id, p.sku, p.id)}
                  style={{ color: '#1a237e', textDecoration: 'underline', cursor: 'pointer' }}
                >
                  {p.nombre || p.sku}
                </span>
              ) : (
                <span>{p.nombre || p.sku}</span>
              )}
            </React.Fragment>
          ))}
          {prov ? ` · ${prov}` : ''}
        </span>
      )
    }
    return <span>{ambitoText(c)}</span>
  }

  if (loading) return null
  if (!publicos.length && !usos.length) {
    return (
      <div style={{ padding: '32px 16px', textAlign: 'center', color: '#666' }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#1a237e' }}>Aún no tienes cupones</div>
        <div style={{ fontSize: 13, marginTop: 6, lineHeight: 1.4 }}>
          No hay cupones disponibles para tu comuna por ahora, ni cupones usados en tu historial.
          Cuando haya nuevos, aparecerán acá.
        </div>
      </div>
    )
  }

  return (
    <div style={{ padding: '12px 16px' }}>
      {publicos.length > 0 && (
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1a237e', margin: '4px 0 8px' }}>
            Cupones disponibles
          </h3>
          {publicos.map((c: any) => {
            const fb = feedback[c.codigo]
            return (
              <div
                key={c._id}
                style={{ border: '1px solid #e0e0e0', borderRadius: 10, padding: 12, marginBottom: 8 }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 700, fontSize: 15 }}>{c.codigo}</span>
                  <span
                    style={{
                      background: '#e8f5e9',
                      color: '#2e7d32',
                      borderRadius: 6,
                      padding: '2px 8px',
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    {descuentoText(c)}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: '#333', marginTop: 6, fontWeight: 600 }}>{renderAmbito(c)}</div>
                <div style={{ fontSize: 12, color: '#666', marginTop: 3 }}>
                  {c.monto_minimo ? `Mín. ${formatCurrency(c.monto_minimo)}` : 'Sin mínimo'}
                  {c.fecha_fin ? ` · Hasta ${new Date(c.fecha_fin).toLocaleDateString()}` : ''}
                </div>
                <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{usosText(c)}</div>
                <div style={{ fontSize: 12, color: '#555', marginTop: 2, fontStyle: 'italic' }}>
                  {comunasText(c)}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
                  <button
                    onClick={() => aplicar(c)}
                    disabled={applying === c.codigo}
                    style={{
                      background: '#1a237e',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 6,
                      padding: '8px 18px',
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: 'pointer',
                      opacity: applying === c.codigo ? 0.6 : 1,
                    }}
                  >
                    {applying === c.codigo ? 'Aplicando…' : 'Aplicar al carro'}
                  </button>
                  {fb && (
                    <span style={{ fontSize: 13, color: fb.ok ? '#2e7d32' : '#c62828', fontWeight: 600 }}>
                      {fb.text}
                    </span>
                  )}
                </div>

                {fb && fb.ok && history && (
                  <div
                    onClick={() => history.push('/cart')}
                    style={{ fontSize: 13, color: '#1a237e', fontWeight: 600, marginTop: 6, cursor: 'pointer' }}
                  >
                    Ir al carro →
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {publicos.length === 0 && (
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1a237e', margin: '4px 0 8px' }}>
            Cupones disponibles
          </h3>
          <div
            style={{
              padding: '16px 12px',
              textAlign: 'center',
              color: '#666',
              fontSize: 13,
              border: '1px dashed #ddd',
              borderRadius: 10,
            }}
          >
            No tienes cupones disponibles por ahora.
          </div>
        </div>
      )}

      {usos.length > 0 && (
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1a237e', margin: '12px 0 8px' }}>
            Mis cupones usados
          </h3>
          {usos.map((u: any) => (
            <div
              key={u._id}
              style={{
                border: '1px solid #eee',
                borderRadius: 10,
                padding: 12,
                marginBottom: 8,
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <span style={{ fontWeight: 600 }}>{u?.cupon_id?.codigo || u?.codigo || 'Cupón'}</span>
              <span style={{ color: u?.estado === 'revertido' ? '#c62828' : '#2e7d32', fontSize: 13 }}>
                {u?.estado === 'revertido' ? 'Revertido' : `-${formatCurrency(u?.descuento_aplicado || 0)}`}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default CouponV2Section
